import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useErrand, ErrandStop, ErrandExtraCharges } from '../contexts/ErrandContext';
import StashitLogo from '../components/Logo/StashitLogo';
import EmptyState from '../components/UI/EmptyState';
import { ErrandCardSkeleton } from '../components/UI/LoadingSkeleton';
import { getErrorMessage } from '../utils/errorMessages';
import { showError, showSuccess } from '../utils/toast';
import ErrandTimeline from '../components/Errand/ErrandTimeline';
import LiveTrackingMap from '../components/Map/LiveTrackingMap';
import CancelErrandModal from '../components/Errand/CancelErrandModal';
import RatingModal from '../components/Errand/RatingModal';

const CustomerErrandTrackingPage: React.FC = () => {
  const { user } = useAuth();
  const {
    userErrands,
    fetchUserErrands,
    loading,
    error,
    cancelErrand,
    submitRating,
    errandStops,
    errandExtraCharges,
    fetchErrandStops,
    getErrandExtraCharges
  } = useErrand();
  const [selectedErrand, setSelectedErrand] = useState<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showRatingModal, setShowRatingModal] = useState(false);
  const [selectedErrandStops, setSelectedErrandStops] = useState<ErrandStop[]>([]);
  const [selectedErrandExtraCharges, setSelectedErrandExtraCharges] = useState<ErrandExtraCharges | null>(null);

  useEffect(() => {
    if (user) {
      fetchUserErrands();
    }
  }, [user, fetchUserErrands]);

  // Load multi-stop stops and extra charges when errand is selected
  useEffect(() => {
    if (selectedErrand) {
      // Load stops for multi-stop errands
      if (selectedErrand.is_multi_stop) {
        fetchErrandStops(selectedErrand.id);
      }

      // Load extra charges
      getErrandExtraCharges(selectedErrand.id);
    }
  }, [selectedErrand, fetchErrandStops, getErrandExtraCharges]);

  // Update local state when context data changes
  useEffect(() => {
    if (selectedErrand) {
      setSelectedErrandStops(errandStops[selectedErrand.id] || []);
      setSelectedErrandExtraCharges(errandExtraCharges[selectedErrand.id] || null);
    }
  }, [selectedErrand, errandStops, errandExtraCharges]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'accepted':
        return 'bg-blue-100 text-blue-800';
      case 'in_progress':
        return 'bg-purple-100 text-purple-800';
      case 'picked_up':
      case 'arrived':
        return 'bg-indigo-100 text-indigo-800';
      case 'completed':
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'cancelled':
      case 'skipped':
        return 'bg-red-100 text-red-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending':
        return '⏳';
      case 'accepted':
        return '✅';
      case 'in_progress':
        return '🚚';
      case 'picked_up':
        return '📦';
      case 'completed':
        return '🎉';
      case 'cancelled':
        return '❌';
      case 'failed':
        return '⚠️';
      default:
        return '📋';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-BW', {
      style: 'currency',
      currency: 'BWP'
    }).format(amount);
  };

  // Filter errands based on search query
  const filteredErrands = useMemo(() => {
    if (!searchQuery.trim()) return userErrands;

    const query = searchQuery.toLowerCase();
    return userErrands.filter(errand =>
      errand.title.toLowerCase().includes(query) ||
      errand.description?.toLowerCase().includes(query) ||
      errand.pickup_location.toLowerCase().includes(query) ||
      errand.dropoff_location?.toLowerCase().includes(query) ||
      errand.status.toLowerCase().includes(query)
    );
  }, [userErrands, searchQuery]);

  // Show error toast if error exists
  useEffect(() => {
    if (error) {
      showError(getErrorMessage(error, 'Failed to load errands'));
    }
  }, [error]);

  if (loading && userErrands.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="space-y-4">
            <ErrandCardSkeleton />
            <ErrandCardSkeleton />
            <ErrandCardSkeleton />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-yellow-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <StashitLogo className="h-8 w-auto" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">My Errands</h1>
                <p className="text-gray-600">Track your errand progress</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Total Errands</p>
              <p className="text-2xl font-bold text-blue-600">{userErrands.length}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8 overflow-x-hidden">
        {userErrands.length === 0 ? (
          <EmptyState
            icon="📦"
            title="No Errands Yet"
            message="You haven't created any errands yet. Start by requesting your first errand!"
            actionLabel="Create Your First Errand"
            actionPath="/errand/request"
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-8">
            {/* Errands List */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 gap-2">
                    <div className="min-w-0 flex-1">
                      <h2 className="text-base sm:text-lg font-semibold text-gray-900">Your Errands</h2>
                      <p className="text-xs sm:text-sm text-gray-600">Click on an errand to see details</p>
                    </div>
                    {userErrands.length > 0 && (
                      <div className="text-xs sm:text-sm text-gray-500 whitespace-nowrap">
                        {filteredErrands.length} of {userErrands.length}
                      </div>
                    )}
                  </div>
                  {/* Search Bar */}
                  {userErrands.length > 0 && (
                    <div className="relative">
                      <input
                        type="text"
                        placeholder="Search errands by title, location, or status..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      />
                      <div className="absolute left-3 top-2.5 text-gray-400">
                        🔍
                      </div>
                      {searchQuery && (
                        <button
                          onClick={() => setSearchQuery('')}
                          className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-600"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  )}
                </div>
                {filteredErrands.length === 0 && searchQuery ? (
                  <div className="p-12">
                    <EmptyState
                      icon="🔍"
                      title="No Results Found"
                      message={`No errands match "${searchQuery}". Try a different search term.`}
                      onAction={() => setSearchQuery('')}
                      actionLabel="Clear Search"
                    />
                  </div>
                ) : (
                  <div className="divide-y divide-gray-200">
                    {filteredErrands.map((errand) => (
                    <div
                      key={errand.id}
                      className={`p-4 sm:p-6 cursor-pointer hover:bg-gray-50 transition-colors ${
                        selectedErrand?.id === errand.id ? 'bg-blue-50 border-l-4 border-blue-500' : ''
                      }`}
                      onClick={() => setSelectedErrand(errand)}
                    >
                      <div className="flex flex-col sm:flex-row items-start sm:items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-2">
                            <span className="text-xl sm:text-2xl">{getStatusIcon(errand.status)}</span>
                            <h3 className="font-semibold text-gray-900 truncate text-sm sm:text-base">{errand.title}</h3>
                            <span className={`px-2 sm:px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(errand.status)}`}>
                              {errand.status.replace('_', ' ')}
                            </span>
                          </div>
                          <p className="text-gray-600 mb-2 text-sm sm:text-base line-clamp-2">{errand.description}</p>
                          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4 gap-1 sm:gap-0 text-xs sm:text-sm text-gray-500">
                            <span className="truncate">📍 {errand.pickup_location}</span>
                            {errand.dropoff_location && (
                              <span className="truncate hidden sm:inline">→ {errand.dropoff_location}</span>
                            )}
                            <span className="whitespace-nowrap">💰 {formatCurrency(errand.total_cost || errand.estimated_value || 0)}</span>
                          </div>
                        </div>
                        <div className="text-left sm:text-right flex-shrink-0">
                          <p className="text-xs sm:text-sm text-gray-500">{formatDate(errand.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                )}
              </div>
            </div>

            {/* Errand Details */}
            <div className="lg:col-span-1">
              {selectedErrand ? (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                  <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Errand Details</h3>
                  </div>
                  <div className="p-6 space-y-4">
                    {/* Timeline View */}
                    <div className="mb-6">
                      <ErrandTimeline
                        currentStatus={selectedErrand.status}
                        createdAt={selectedErrand.created_at}
                        acceptedAt={selectedErrand.accepted_at}
                        inProgressAt={selectedErrand.in_progress_at}
                        pickedUpAt={selectedErrand.picked_up_at}
                        completedAt={selectedErrand.completed_at}
                        cancelledAt={selectedErrand.cancelled_at}
                      />
                    </div>

                    {/* Live Tracking Map (if errand is in progress) */}
                    {((selectedErrand.status === 'accepted' || selectedErrand.status === 'in_progress' || selectedErrand.status === 'picked_up') &&
                     selectedErrand.pickup_latitude && selectedErrand.pickup_longitude) && (
                      <div className="mb-6">
                        <h4 className="font-medium text-gray-900 mb-3">Live Tracking</h4>
                        <LiveTrackingMap
                          pickupLocation={{
                            lat: selectedErrand.pickup_latitude,
                            lng: selectedErrand.pickup_longitude
                          }}
                          dropoffLocation={
                            selectedErrand.dropoff_latitude && selectedErrand.dropoff_longitude
                              ? {
                                  lat: selectedErrand.dropoff_latitude,
                                  lng: selectedErrand.dropoff_longitude
                                }
                              : undefined
                          }
                          driverLocation={
                            selectedErrand.driver_location_lat && selectedErrand.driver_location_lng
                              ? {
                                  lat: selectedErrand.driver_location_lat,
                                  lng: selectedErrand.driver_location_lng
                                }
                              : undefined
                          }
                          showRoute={true}
                          height="300px"
                        />
                        <p className="text-xs text-gray-500 mt-2">
                          {selectedErrand.driver_location_lat ? 'Driver location is being tracked' : 'Driver location will appear when available'}
                        </p>
                      </div>
                    )}

                    {/* Multi-stop Progress */}
                    {selectedErrand.is_multi_stop && selectedErrandStops.length > 0 && (
                      <div className="mb-6">
                        <h4 className="font-medium text-gray-900 mb-3">Stops</h4>
                        <ol className="relative border-l border-gray-200 dark:border-gray-700">
                          {selectedErrandStops.map((stop, index) => (
                            <li key={stop.id || index} className="mb-4 ml-4">
                              <div className={`absolute w-3 h-3 rounded-full mt-1.5 -left-1.5 border border-white ${stop.status === 'completed' ? 'bg-green-500' : 'bg-gray-400'}`}></div>
                              <p className="font-semibold capitalize">{stop.stop_type} #{stop.sequence_number}</p>
                              <p className="text-sm text-gray-700">{stop.location_name}</p>
                              <p className={`text-xs font-medium capitalize px-2 py-0.5 rounded inline-block ${getStatusColor(stop.status)}`}>
                                {stop.status}
                              </p>
                            </li>
                          ))}
                        </ol>
                      </div>
                    )}

                    {/* Errand Information */}
                    <div className="space-y-3">
                      <div>
                        <span className="font-medium text-gray-900">Type:</span>
                        <p className="text-gray-600">{selectedErrand.errand_types?.name || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Payment Method:</span>
                        <p className="text-gray-600 capitalize">{selectedErrand.payment_method.replace('_', ' ')}</p>
                      </div>
                      <div>
                        <span className="font-medium text-gray-900">Estimated Value:</span>
                        <p className="text-gray-600">{formatCurrency(selectedErrand.estimated_value || 0)}</p>
                      </div>
                      {selectedErrand.total_cost && (
                        <div>
                          <span className="font-medium text-gray-900">Total Cost:</span>
                          <p className="text-gray-600">{formatCurrency(selectedErrand.total_cost)}</p>
                        </div>
                      )}
                      <div>
                        <span className="font-medium text-gray-900">Created:</span>
                        <p className="text-gray-600">{formatDate(selectedErrand.created_at)}</p>
                      </div>
                      {selectedErrand.completed_at && (
                        <div>
                          <span className="font-medium text-gray-900">Completed:</span>
                          <p className="text-gray-600">{formatDate(selectedErrand.completed_at)}</p>
                        </div>
                      )}
                    </div>

                    {/* Extra Charges Information */}
                    {selectedErrandExtraCharges && selectedErrandExtraCharges.summary.total_approved_amount > 0 && (
                      <div className="pt-4 border-t border-gray-200">
                        <h4 className="font-medium text-gray-900 mb-2">Extra Charges</h4>
                        <div className="space-y-2">
                          {selectedErrandExtraCharges.extra_charges
                            .filter(charge => charge.status === 'approved' || charge.status === 'paid')
                            .map(charge => (
                              <div key={charge.id} className="flex justify-between items-center text-sm">
                                <p className="text-gray-600">{charge.description}</p>
                                <p className="font-medium text-gray-800">{formatCurrency(charge.amount)}</p>
                              </div>
                            ))}
                          <div className="flex justify-between items-center font-bold text-sm pt-2 border-t border-gray-100">
                            <p>Total Approved Charges</p>
                            <p>{formatCurrency(selectedErrandExtraCharges.summary.total_approved_amount)}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Special Instructions */}
                    {selectedErrand.special_instructions && (
                      <div>
                        <span className="font-medium text-gray-900">Special Instructions:</span>
                        <p className="text-gray-600 mt-1">{selectedErrand.special_instructions}</p>
                      </div>
                    )}

                    {/* Action Buttons */}
                    {selectedErrand.status === 'pending' && (
                      <div className="pt-4 border-t border-gray-200 mb-4">
                        <button
                          onClick={() => setShowCancelModal(true)}
                          className="btn-danger w-full"
                        >
                          Cancel Errand
                        </button>
                      </div>
                    )}

                    {selectedErrand.status === 'completed' && selectedErrand.driver_id && (
                      <div className="pt-4 border-t border-gray-200 mb-4">
                        <button
                          onClick={() => setShowRatingModal(true)}
                          className="btn-tertiary w-full"
                        >
                          ⭐ Rate Driver
                        </button>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="pt-4 border-t border-gray-200">
                      <Link
                        to="/chat"
                        className="btn-primary w-full text-center block"
                      >
                        💬 Chat with Driver
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 text-center">
                  <div className="text-gray-400 text-4xl mb-4">👆</div>
                  <h3 className="font-medium text-gray-900 mb-2">Select an Errand</h3>
                  <p className="text-gray-600 text-sm">Click on an errand from the list to see details and track progress</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Cancel Errand Modal */}
      {selectedErrand && (
        <CancelErrandModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onConfirm={async (reason, otherReason) => {
            const success = await cancelErrand(selectedErrand.id, reason, otherReason);
            if (success) {
              setShowCancelModal(false);
              await fetchUserErrands();
              setSelectedErrand(null);
            }
          }}
          errandTitle={selectedErrand.title}
        />
      )}

      {/* Rating Modal */}
      {selectedErrand && selectedErrand.driver_id && (
        <RatingModal
          isOpen={showRatingModal}
          onClose={() => setShowRatingModal(false)}
          errandId={selectedErrand.id}
          rateeId={selectedErrand.driver_id}
          rateeName={selectedErrand.driver_profiles?.full_name || 'Driver'}
          ratingType="customer_to_driver"
          onRatingSubmitted={() => {
            // Refresh errands to update status
            fetchUserErrands();
          }}
        />
      )}
    </div>
  );
};

export default CustomerErrandTrackingPage;
